import React, { useMemo, useState } from 'react';
import PaymentForm, { FreeOrderConfirm } from '../../components/PaymentForm.jsx';
import { labelClass, inputClass, selectClass, primaryBtnClass, STATES } from '../Signup/shared.jsx';
import { getCsrfToken } from '../../lib/csrf.js';

function formatPrice(cents) {
  if (cents == null) return '$0';
  return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

// The offer itself is fixed server-side — this page never lets the customer
// change items, quantities, or price. It collects only the buyer's own
// details, then swaps in the standard payment element.
export default function DealShow({
  token, name, plan, facility_type_label,
  items = [], price_cents, list_subtotal_cents, publishable_key,
  mode = 'new_buyer', target_facility = null, refusal = null,
}) {
  if (refusal) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6">
        <div className="max-w-lg mx-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">This offer isn't for this account</h1>
          <p className="mt-2 text-sm text-gray-600">{refusal.message}</p>
        </div>
      </div>
    );
  }

  return <DealOffer {...{ token, name, plan, facility_type_label, items, price_cents,
                          list_subtotal_cents, publishable_key, mode, target_facility }} />;
}

function DealOffer({
  token, name, plan, facility_type_label,
  items = [], price_cents, list_subtotal_cents, publishable_key,
  mode, target_facility,
}) {
  const isFacilityMode = mode === 'facility';
  const [buyer, setBuyer] = useState({ first_name: '', last_name: '', email: '' });
  const [facility, setFacility] = useState({ name: '', address: '', city: '', state: 'CA', zip: '', license_number: '' });
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const savings = useMemo(
    () => Math.max(0, (list_subtotal_cents || 0) - (price_cents || 0)),
    [list_subtotal_cents, price_cents]
  );

  const detailsComplete = useMemo(() => {
    const buyerOk = buyer.first_name.trim() && buyer.last_name.trim() && /.+@.+\..+/.test(buyer.email);
    return Boolean(
      buyerOk && facility.name.trim() && facility.address.trim() &&
      facility.city.trim() && facility.state && /^\d{5}(-\d{4})?$/.test(facility.zip)
    );
  }, [buyer, facility]);

  const setBuyerField = (field) => (e) => setBuyer((b) => ({ ...b, [field]: e.target.value }));
  const setFacilityField = (field) => (e) => setFacility((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!isFacilityMode && !detailsComplete) || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/deals/${token}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        // A targeted deal is fully described server-side; sending buyer or
        // facility fields here would be ignored.
        body: JSON.stringify(isFacilityMode ? {} : { buyer, facility }),
      });
      const data = await response.json();
      if (response.ok) {
        setSession(data);
      } else {
        setError(data?.error || 'We could not start checkout. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium">
            Your quoted offer
          </span>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{name}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {isFacilityMode
              ? `Prepared for ${target_facility?.name}. This price is locked in — nothing here can be changed.`
              : `Prepared for ${facility_type_label}. This price is locked in — nothing here can be changed.`}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">What's included</h2>
              <div className="divide-y divide-gray-100">
                {items.map((item) => (
                  <div key={item.name} className="py-2 flex justify-between gap-3 text-sm">
                    <span className="text-gray-900 min-w-0 break-words">{item.name}</span>
                    <span className="shrink-0 text-gray-500">×{item.quantity}</span>
                  </div>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5 text-sm">
                {list_subtotal_cents > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>Standard price</span>
                    <span className="line-through">{formatPrice(list_subtotal_cents)}</span>
                  </div>
                )}
                {savings > 0 && (
                  <div className="flex justify-between font-medium text-green-700">
                    <span>Your discount</span><span>−{formatPrice(savings)}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-200 flex justify-between items-center">
                  <span className="font-semibold text-gray-900">
                    {plan === 'subscription' ? 'Due today' : 'Total'}
                  </span>
                  <span className="text-xl font-bold text-indigo-600">{formatPrice(price_cents)}</span>
                </div>
              </div>

              {plan === 'subscription' && (
                <p className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                  This offer applies to your first payment. The subscription then renews at the
                  standard price of {formatPrice(list_subtotal_cents)} unless cancelled.
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
              )}

              {session ? (
                session.client_secret ? (
                  <PaymentForm
                    clientSecret={session.client_secret}
                    publishableKey={session.publishable_key || publishable_key}
                    returnUrl={session.return_url}
                    submitLabel={`Pay ${formatPrice(session.amount_cents)}`}
                    intent={session.mode === 'subscription' ? 'payment' : 'payment'}
                  />
                ) : (
                  <FreeOrderConfirm returnUrl={session.return_url} reason="deal" />
                )
              ) : isFacilityMode ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h2 className="text-sm font-semibold text-gray-800">Claim this offer</h2>
                  <p className="text-sm text-gray-600">
                    These seats will be added to{' '}
                    <span className="font-semibold text-gray-900">{target_facility?.name}</span>'s pool,
                    ready for you to assign to your team.
                  </p>
                  {plan === 'subscription' && (
                    <p className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600">
                      This starts a new subscription alongside any you already have.
                    </p>
                  )}
                  <button type="submit" className={`${primaryBtnClass} w-full`} disabled={submitting}>
                    {submitting ? 'Preparing checkout…' : `Continue to payment — ${formatPrice(price_cents)}`}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h2 className="text-sm font-semibold text-gray-800">Your details</h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass} htmlFor="first_name">First name</label>
                      <input id="first_name" className={inputClass} value={buyer.first_name}
                             onChange={setBuyerField('first_name')} required autoComplete="given-name" />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="last_name">Last name</label>
                      <input id="last_name" className={inputClass} value={buyer.last_name}
                             onChange={setBuyerField('last_name')} required autoComplete="family-name" />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass} htmlFor="email">Email</label>
                    <input id="email" type="email" className={inputClass} value={buyer.email}
                           onChange={setBuyerField('email')} required autoComplete="email" />
                    <p className="mt-1 text-xs text-gray-500">You'll use this to sign in after payment.</p>
                  </div>

                  <>
                      <h2 className="text-sm font-semibold text-gray-800 pt-2">Your facility</h2>
                      <div>
                        <label className={labelClass} htmlFor="facility_name">Facility name</label>
                        <input id="facility_name" className={inputClass} value={facility.name}
                               onChange={setFacilityField('name')} required />
                      </div>
                      <div>
                        <label className={labelClass} htmlFor="address">Address</label>
                        <input id="address" className={inputClass} value={facility.address}
                               onChange={setFacilityField('address')} required autoComplete="street-address" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-1">
                          <label className={labelClass} htmlFor="city">City</label>
                          <input id="city" className={inputClass} value={facility.city}
                                 onChange={setFacilityField('city')} required />
                        </div>
                        <div>
                          <label className={labelClass} htmlFor="state">State</label>
                          <select id="state" className={selectClass} value={facility.state}
                                  onChange={setFacilityField('state')} required>
                            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass} htmlFor="zip">ZIP</label>
                          <input id="zip" className={inputClass} value={facility.zip}
                                 onChange={setFacilityField('zip')} required inputMode="numeric" />
                        </div>
                      </div>
                      <div>
                        <label className={labelClass} htmlFor="license_number">
                          Facility license number <span className="font-normal text-gray-400">(optional)</span>
                        </label>
                        <input id="license_number" className={inputClass} value={facility.license_number}
                               onChange={setFacilityField('license_number')} />
                      </div>
                  </>

                  <button type="submit" className={`${primaryBtnClass} w-full`} disabled={!detailsComplete || submitting}>
                    {submitting ? 'Preparing checkout…' : 'Continue to payment'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
